import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  divider,
  text,
} from '@groovehq/internal-design-system/lib/styles/elements'
import { styles as modalBtnsStyles } from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns.styles'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import { doFetchBillingData } from 'ducks/billing'
import { useBilling, usePlanChangeInfo } from 'ducks/billing/hooks'
import {
  DRAWER_TYPE_BILLING_DOWNGRADE_PLAN_CONFIRMATION,
  DRAWER_TYPE_BILLING_EXTERNAL_APPROVE,
  DRAWER_TYPE_BILLING_DOWNGRADE_PLAN,
} from 'ducks/drawers/types'
import { useDrawer } from 'ducks/drawers/hooks'
import { strftime } from 'util/date'
import { getFormattedPrice } from 'util/numbers'
import debug from 'util/debug'
import { buildDrawerQueryParam } from 'ducks/drawers/util'
import { INBOX, KB } from 'ducks/billing/productTypes'
import { AdminAccessDrawer } from '../../NoAccess'
import PlanRow from '../PlanRow'
import { styles } from '../styles'

const PRODUCT_TO_PRODUCT_NAME = {
  [KB]: 'Knowledge base',
  [INBOX]: app.t('Mailbox'),
}

const BillingDownGradePlanOverview = ({
  open,
  onExit,
  onClose,
  drawerResourceId: planId,
  drawerProductType,
  drawerLimitsConfirmed = false,
  drawerBillingCycle,
}) => {
  const dispatch = useDispatch()

  const planChangeInfo = usePlanChangeInfo(
    planId,
    drawerBillingCycle,
    drawerProductType
  )

  const {
    current: {
      displayName: currentPlanDisplayName,
      plan: currentPlan,
      lineItem: { itemPrice = 0, cyclePrice = 0 },
      billingCycle: currentBillingCycle,
    },
    new: {
      displayName: newPlanDisplayName,
      plan: newPlan,
      lineItem: { itemPrice: newItemPrice = 0, cyclePrice: newCyclePrice = 0 },
      billingCycle: newBillingCycle,
      isPlanLimitsExceeded,
    },
    billingEstimate: { nextBillDate, currency = 'USD' },
    isCycleChange,
    isUpgradingToAnnual,
    currentUsageUsers,
    includeOtherProductInBreakdown,
    otherProduct: {
      productType: otherProductType,
      billingCycle: otherProductBillingCycle,
      planId: otherProductPlanId,
    },
  } = planChangeInfo

  const { currentInbox, currentKb, updateSubscription } = useBilling()

  const otherProductPlanChangeInfo = usePlanChangeInfo(
    otherProductPlanId,
    otherProductBillingCycle,
    otherProductType
  )

  const {
    current: {
      displayName: currentOtherProductPlanDisplayName,
      plan: currentOtherProductPlan,
      lineItem: {
        itemPrice: currentOtherProductItemPrice = 0,
        cyclePrice: currentOtherProductCyclePrice = 0,
      },
      billingCycle: currentOtherProductBillingCycle,
    },
    new: {
      displayName: newOtherProductPlanDisplayName,
      plan: newOtherProductPlan,
      lineItem: {
        itemPrice: newOtherProductItemPrice = 0,
        cyclePrice: newOtherProductCyclePrice = 0,
      },
      billingCycle: newOtherProductBillingCycle,
    },
    isUpgradingToAnnual: otherProductIsUpgradingToAnnual,
  } = otherProductPlanChangeInfo

  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    drawerId: downgradePlanDrawerId,
    openDrawer: openDowngradePlan,
  } = useDrawer({
    type: DRAWER_TYPE_BILLING_DOWNGRADE_PLAN,
  })

  const { openDrawer: openBillingExternalApprove } = useDrawer({
    type: DRAWER_TYPE_BILLING_EXTERNAL_APPROVE,
  })

  const {
    drawerId: billingDowngradeConfirmationDrawerId,
    openDrawer: openBillingDowngradeConfirmation,
  } = useDrawer({
    type: DRAWER_TYPE_BILLING_DOWNGRADE_PLAN_CONFIRMATION,
  })

  const handleClose = useCallback(
    () => {
      onClose({
        ignoreStack: true,
      })
    },
    [onClose]
  )

  const onSubmit = useCallback(
    async () => {
      setIsSubmitting(true)
      const inboxPlanId =
        (drawerProductType === 'inbox' && planId) || currentInbox.plan.planId
      const kbPlanId =
        (drawerProductType === 'kb' && planId) || currentKb.plan.planId

      const data = {
        inboxPlanId,
        kbPlanId,
        billingCycle: newBillingCycle,
      }
      try {
        const response = await updateSubscription(data, {
          successMessageDisabled: true,
          failed: 'Your downgrade has failed',
          convertTrials: false,
        })
        const {
          updateSubscription: {
            pendingSubscription: { approvalUrl } = {},
          } = {},
        } =
          response || {}
        if (approvalUrl) {
          openBillingExternalApprove()
        } else {
          openBillingDowngradeConfirmation(planId, {
            query: {
              ...buildDrawerQueryParam(
                billingDowngradeConfirmationDrawerId,
                'drawerProductType',
                drawerProductType
              ),
              ...buildDrawerQueryParam(
                billingDowngradeConfirmationDrawerId,
                'drawerOtherProductPlanId',
                otherProductPlanId
              ),
              ...buildDrawerQueryParam(
                billingDowngradeConfirmationDrawerId,
                'drawerOtherProductProductType',
                otherProductType
              ),
            },
          })
        }
      } catch (error) {
        debug(error)
        setIsSubmitting(false)
      }
    },
    [
      updateSubscription,
      openBillingExternalApprove,
      newBillingCycle,
      currentKb.plan.planId,
      currentInbox.plan.planId,
      openBillingDowngradeConfirmation,
      drawerProductType,
      planId,
      billingDowngradeConfirmationDrawerId,
      otherProductType,
      otherProductPlanId,
    ]
  )

  const footer = (
    <div css={[modalBtnsStyles.base, styles.btns]}>
      <Button type="tertiary" size="big" onClick={handleClose}>
        No thanks, I’d like to keep my current plan
      </Button>
      <Button type="info" size="big" onClick={onSubmit} disabled={isSubmitting}>
        Downgrade now
      </Button>
    </div>
  )

  useEffect(
    () => {
      dispatch(doFetchBillingData())
    },
    [dispatch]
  )

  useEffect(
    () => {
      if (!newPlan) return
      if (isPlanLimitsExceeded && !drawerLimitsConfirmed) {
        openDowngradePlan(planId, {
          query: {
            ...buildDrawerQueryParam(
              downgradePlanDrawerId,
              'drawerProductType',
              drawerProductType
            ),
            ...buildDrawerQueryParam(
              downgradePlanDrawerId,
              'drawerBillingCycle',
              drawerBillingCycle
            ),
          },
        })
      }
    },
    [
      newPlan,
      openDowngradePlan,
      downgradePlanDrawerId,
      planId,
      drawerProductType,
      drawerLimitsConfirmed,
      drawerBillingCycle,
      isPlanLimitsExceeded,
    ]
  )

  useEffect(
    () => {
      if (!planId || !newPlan) {
        onExit()
      }
    },
    [planId, onExit, newPlan]
  )

  const totalNewCyclePrice = useMemo(
    () => {
      if (!includeOtherProductInBreakdown) return newCyclePrice

      return newCyclePrice + newOtherProductCyclePrice
    },
    [includeOtherProductInBreakdown, newCyclePrice, newOtherProductCyclePrice]
  )

  if (!newPlan) return null

  return (
    <AdminAccessDrawer
      title="Downgrade plan"
      open={open}
      footer={footer}
      onClose={onExit}
    >
      <div className="grui pt-12" css={styles.container}>
        <div css={text.styles.textNormal}>
          You are about to schedule the <strong>Downgrade</strong> of your{' '}
          <strong>
            {currentBillingCycle} subscription from {currentPlanDisplayName} to{' '}
            {newPlanDisplayName} {isCycleChange ? newBillingCycle : ''}
          </strong>
        </div>
        <div
          css={styles.planChangesCard}
          className="grui pt-10 mt-12 -ml-7 -mr-7"
        >
          {includeOtherProductInBreakdown && (
            <div css={[text.styles.text2Xs, styles.tag]}>
              {PRODUCT_TO_PRODUCT_NAME[drawerProductType]}
            </div>
          )}
          <div className="grui pt-2 px-7">
            <PlanRow
              name={currentPlanDisplayName}
              product={currentPlan.product}
              cyclePrice={cyclePrice}
              itemPrice={itemPrice}
              users={currentUsageUsers}
              className="grui mb-8"
              planCycle={currentBillingCycle}
              isCycleChange={isCycleChange}
              isUpgradingToAnnual={isUpgradingToAnnual}
              pricingModel={currentPlan.pricingModel}
            />
            <PlanRow
              name={newPlanDisplayName}
              product={newPlan.product}
              cyclePrice={newCyclePrice}
              itemPrice={newItemPrice}
              users={currentUsageUsers}
              planCycle={newBillingCycle}
              isCycleChange={isCycleChange}
              isUpgradingToAnnual={isUpgradingToAnnual}
              pricingModel={
                isUpgradingToAnnual
                  ? currentPlan.pricingModel
                  : newPlan?.pricingModel
              }
              isNew
            />
          </div>
          <div
            role="separator"
            css={divider.styles.base}
            className="grui my-9"
          />
          {includeOtherProductInBreakdown && (
            <>
              <div css={[text.styles.text2Xs, styles.tag]}>
                {PRODUCT_TO_PRODUCT_NAME[otherProductType]}
              </div>
              <div className="grui pt-2 px-7">
                <PlanRow
                  name={currentOtherProductPlanDisplayName}
                  product={currentOtherProductPlan.product}
                  cyclePrice={currentOtherProductCyclePrice}
                  itemPrice={currentOtherProductItemPrice}
                  users={currentUsageUsers}
                  className="grui mb-8"
                  pricingModel={currentOtherProductPlan.pricingModel}
                  planCycle={currentOtherProductBillingCycle}
                  isCycleChange={isCycleChange}
                  isUpgradingToAnnual={otherProductIsUpgradingToAnnual}
                />
                <PlanRow
                  name={newOtherProductPlanDisplayName}
                  product={newOtherProductPlan.product}
                  cyclePrice={newOtherProductCyclePrice}
                  itemPrice={newOtherProductItemPrice}
                  users={currentUsageUsers}
                  pricingModel={
                    otherProductIsUpgradingToAnnual
                      ? currentOtherProductPlan.pricingModel
                      : newOtherProductPlan?.pricingModel
                  }
                  planCycle={newOtherProductBillingCycle}
                  isCycleChange={isCycleChange}
                  isUpgradingToAnnual={otherProductIsUpgradingToAnnual}
                  isNew
                />
              </div>
              <div
                role="separator"
                css={divider.styles.base}
                className="grui my-9"
              />
            </>
          )}
          <div className="grui px-7 py-9">
            <div css={[styles.row, text.styles.fontMedium]}>
              <div>Total for your next recurring invoice</div>
              <div>{getFormattedPrice(totalNewCyclePrice, currency)}</div>
            </div>
            <div css={text.styles.text2Xs} className="grui opacity-80 mt-2">
              Bill on {strftime('%A, %Do %B ‘%y', nextBillDate * 1000)}
            </div>
          </div>
        </div>
      </div>
    </AdminAccessDrawer>
  )
}

export default BillingDownGradePlanOverview
